import { useTranslation } from 'react-i18next'

import type { CappingAlias } from '@/services/networks/networkFieldSettings'
import {
  isBudgetLimit,
  rateLimitAmount,
  rateLimitCurrency,
  type RateLimit,
  type RateLimitScope,
} from '@/services/earnings/rateLimits'
import { formatNumber } from '../../utils/format'

/**
 * What a network calls its delivery caps, and how one reads as a sentence.
 *
 * Networks name the same feature three ways — "rate limit", "cap" or
 * "frequency cap" — picked in the network's own settings, so every label is
 * looked up through the alias rather than hard-coded.
 */
export interface CappingText {
  title: string
  name: string
  namePlural: string
  typeField: string
  scopeField: string
  empty: string
  scope: (scope: RateLimitScope) => string
  scopeHelp: (scope: RateLimitScope) => string
  smoothedLabel: string
  smoothedHelp: string
  /** The cap as one line of prose, e.g. "3 impressions every 1 day". */
  describe: (limit: RateLimit, currency?: string) => string
}

export function useCappingText(alias: CappingAlias = '1'): CappingText {
  const { t } = useTranslation()
  const naming = (key: string) => t(`lineItems.capping.naming.${alias}.${key}`)

  const number = (value: number) => formatNumber(value)

  const money = (value: number, currency?: string) =>
    currency
      ? formatNumber(value, { style: 'currency', currency })
      : number(value)

  const describe = (limit: RateLimit, currency?: string) => {
    const amount = rateLimitAmount(limit) ?? 0

    if (isBudgetLimit(limit)) {
      return t('lineItems.capping.summary.budget', {
        amount: money(amount, rateLimitCurrency(limit) || currency),
      })
    }

    const type = t(`lineItems.capping.typeUnit.${limit.type}`, { count: amount })
    const smoothed = limit.smoothed && limit.scope === 'GLOBAL'
    const key = smoothed ? 'smoothedAmount' : 'amount'
    const amountText = t(`lineItems.capping.summary.${key}`, { amount: number(amount), type })

    if (limit.periodType === 'ALL_TIME') {
      return t('lineItems.capping.summary.lifetime', { amount: amountText })
    }
    return t('lineItems.capping.summary.period', {
      amount: amountText,
      period: t(`lineItems.capping.periodUnit.${limit.periodType}`, {
        count: limit.periodAmount,
      }),
      periodAmount: number(limit.periodAmount),
    })
  }

  return {
    title: naming('title'),
    name: naming('name'),
    namePlural: naming('namePlural'),
    typeField: naming('typeField'),
    scopeField: naming('scopeField'),
    empty: naming('empty'),
    scope: (scope) => naming(`scopes.${scope}`),
    scopeHelp: (scope) => naming(`scopeHelp.${scope}`),
    smoothedLabel: naming('smoothed'),
    smoothedHelp: naming('smoothedHelp'),
    describe,
  }
}
